import { generateRandomString } from '@mntn-dev/utilities'
import { EnumBuilder, type ZodSimplify } from '@mntn-dev/utility-types'
import { z } from 'zod'
import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  DescriptionSchema,
  NameSchema,
  TimestampSchema,
  UrlSchema,
} from './property.models.ts'
import {
  OrganizationId,
  OrganizationIdSchema,
  OrganizationUrn,
  OrganizationUrnSchema,
} from './unique-id.models.ts'

export const InternalOrganizationId = OrganizationId.Empty
export const InternalOrganizationUrn = OrganizationUrn(InternalOrganizationId)

/**
 * OrganizationType
 */
export const CustomerOrganizationTypes = ['brand', 'agency'] as const
export const InternalOrganizationTypes = ['internal'] as const

export const [OrganizationTypes, OrganizationTypeSchema, OrganizationTypeEnum] =
  EnumBuilder(...CustomerOrganizationTypes, ...InternalOrganizationTypes)
export type OrganizationType = ZodSimplify<typeof OrganizationTypeSchema>

export const [, CustomerOrganizationTypeSchema, CustomerOrganizationTypeEnum] =
  EnumBuilder(...CustomerOrganizationTypes)
export type CustomerOrganizationType = ZodSimplify<
  typeof CustomerOrganizationTypeSchema
>

export function isCustomerOrganizationType(
  value: string
): value is CustomerOrganizationType {
  return CustomerOrganizationTypeSchema.safeParse(value).success
}

export function isOrganizationType(value: string): value is OrganizationType {
  return OrganizationTypeSchema.safeParse(value).success
}

type OrganizationTypeLike = {
  organizationType: OrganizationType
}

export const hasOrganizationType = <
  Model extends OrganizationTypeLike,
  OrgType extends OrganizationType,
>(
  organization: Model,
  organizationType: OrgType
): organization is Model & { organizationType: OrgType } =>
  organization.organizationType === organizationType

export const OrganizationPayeeIdSchema = z.string().brand('payee')
export type OrganizationPayeeId = ZodSimplify<typeof OrganizationPayeeIdSchema>

export const OrganizationPayeeId = (
  value: string = generateRandomString(16)
) => {
  return OrganizationPayeeIdSchema.parse(value)
}

/**
 * OnboardingStatus
 */
export const [
  OnboardingStatuses,
  OnboardingStatusSchema,
  OnboardingStatusEnum,
] = EnumBuilder('onboarding', 'onboarded', 'interview-failed')

export type OnboardingStatus = ZodSimplify<typeof OnboardingStatusSchema>

export const getInitialOnboardingStatus = (
  type: OrganizationType
): OnboardingStatus => (type === 'agency' ? 'onboarding' : 'onboarded')

/**
 * OnboardingStepStatus
 */
export const [
  OnboardingStepStatuses,
  OnboardingStepStatusSchema,
  OnboardingStepStatusEnum,
] = EnumBuilder('completed', 'pending', 'failed')

export type OnboardingStepStatus = ZodSimplify<
  typeof OnboardingStepStatusSchema
>

/**
 * OnboardingStep
 */
export const [OnboardingSteps, OnboardingStepSchema, OnboardingStepEnum] =
  EnumBuilder('profile-info', 'payee-info', 'certification', 'interview')

export type OnboardingStep = ZodSimplify<typeof OnboardingStepSchema>

export const OrganizationOnboardingDomainSelectModelSchema = z.object({
  status: OnboardingStatusSchema,
  stepsCompleted: z.array(OnboardingStepSchema),
})

export type OrganizationOnboardingDomainSelectModel = ZodSimplify<
  typeof OrganizationOnboardingDomainSelectModelSchema
>

export const OrganizationOnboardingDeltaModelSchema = z.object({
  status: OnboardingStatusSchema.optional(),
  stepsCompleted: z.object({
    added: z.array(OnboardingStepSchema),
    removed: z.array(OnboardingStepSchema),
    same: z.array(OnboardingStepSchema),
  }),
})

export type OrganizationOnboardingDeltaModel = ZodSimplify<
  typeof OrganizationOnboardingDeltaModelSchema
>

export const OrganizationDomainSelectModelSchema = z.object({
  organizationId: OrganizationIdSchema,
  organizationUrn: OrganizationUrnSchema,
  organizationType: OrganizationTypeSchema,
  name: NameSchema,
  description: DescriptionSchema.optional(),
  websiteUrl: UrlSchema.optional(),
  payeeId: OrganizationPayeeIdSchema.optional(),
  isPayable: z.boolean(),
  payeeSyncTimestamp: TimestampSchema.optional(),
  onboarding: OrganizationOnboardingDomainSelectModelSchema,
  isActive: z.boolean(),
  isArchived: z.boolean().optional(),
  isBlocked: z.boolean().optional(),
})

export type OrganizationDomainSelectModel = ZodSimplify<
  typeof OrganizationDomainSelectModelSchema
>

export const OrganizationDomainInsertModelSchema =
  OrganizationDomainSelectModelSchema.pick({
    organizationId: true,
    organizationType: true,
    name: true,
    description: true,
    websiteUrl: true,
  }).partial({
    organizationId: true,
  })

export type OrganizationDomainInsertModel = ZodSimplify<
  typeof OrganizationDomainInsertModelSchema
>

export const OrganizationDomainUpdateModelSchema =
  OrganizationDomainSelectModelSchema.pick({
    name: true,
    description: true,
    websiteUrl: true,
    payeeId: true,
    isPayable: true,
    payeeSyncTimestamp: true,
    isArchived: true,
    isBlocked: true,
  })
    .extend({
      description:
        OrganizationDomainSelectModelSchema.shape.description.nullable(),
      onboarding:
        OrganizationDomainSelectModelSchema.shape.onboarding.partial(),
    })
    .partial()

export type OrganizationDomainUpdateModel = ZodSimplify<
  typeof OrganizationDomainUpdateModelSchema
>

export const OrganizationDomainSelectModelFiltersSchema = z
  .object({
    organizationId: ModelFilterSchema(OrganizationIdSchema),
    // When filtering, transform names to lower case so the
    // "lower(organizations.name)" index can be used.
    name: ModelFilterSchema(NameSchema.toLowerCase()),
    organizationType: ModelFilterSchema(OrganizationTypeSchema),
    onboardingStatus: ModelFilterSchema(OnboardingStatusSchema),
    payeeId: ModelFilterSchema(OrganizationPayeeIdSchema),
    isBlocked: z.boolean(),
    isArchived: z.boolean(),
  })
  .partial()

export type OrganizationDomainSelectModelFilters = ZodSimplify<
  typeof OrganizationDomainSelectModelFiltersSchema
>
